import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TextTableCell from 'components/common/table/TextTableCell';
import { useTranslation } from 'react-i18next';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import CheckboxTableCell from 'components/common/table/CheckboxTableCell';
import DateTableCell from 'components/common/table/DateTableCell';
import IconButton from 'components/common/buttons/IconButton';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_RESIDUAL_VALUE_GRID, DUPLICATE_RESIDUAL_VALUE_GRID } from 'features/residualValueGrid/mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import ListActionsMenu from 'features/common/components/list-actions-menu/ListActionsMenu';
import { stateHistoryResidualValueGridId } from '../store';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_RESIDUAL_VALUE_GRIDS_WRITE } from 'constants/rightsCodes';
import ActionsTableCell from 'components/common/table/ActionsTableCell';

const ItemComponent = ({ data }) => {
  const { t } = useTranslation();
  const historyContext = useHistoryWithContext();
  const confirm = useConfirmDialog();

  const canWrite = usePermission(ADMIN_RESIDUAL_VALUE_GRIDS_WRITE);

  const [pending, setPending] = useState(false);

  const handleEdit = useCallback(() => {
    historyContext.push(`/residualValueGrids/${data.id}`);
  }, [data, historyContext]);

  // #region Delete

  const [deleteResidualValueGrid] = useMutation(DELETE_RESIDUAL_VALUE_GRID, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        setPending(true);
        deleteResidualValueGrid({
          variables: { id: data.id }
        });
      },
      {
        content: t('ResidualValueGrid.ConfirmDelete', { name: data.name })
      }
    );
  }, [confirm, deleteResidualValueGrid, t, data]);

  // #endregion

  // #region Duplicate

  const [duplicateResidualValueGrid] = useMutation(DUPLICATE_RESIDUAL_VALUE_GRID, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDuplicate = useCallback(() => {
    confirm(
      () => {
        setPending(true);
        duplicateResidualValueGrid({
          variables: { id: data.id }
        });
      },
      {
        content: t('ResidualValueGrid.ConfirmDuplication', { name: data.name })
      }
    );
  }, [confirm, duplicateResidualValueGrid, t, data]);

  // #endregion

  const openStateHistory = useCallback(() => {
    stateHistoryResidualValueGridId.set(data.id);
  }, [data]);

  const actions = useMemo(
    () => [
      { caption: 'Delete', handler: handleDelete, icon: 'delete', color: 'error', disabled: !canWrite || !data.isDeletable },
      { caption: 'Duplicate', handler: handleDuplicate, icon: 'file_copy', disabled: !canWrite },
      { caption: 'ResidualValueGrid.ViewStateHistory', handler: openStateHistory, icon: 'history', color: 'warning' }
    ],
    [canWrite, data.isDeletable, handleDelete, handleDuplicate, openStateHistory]
  );

  return (
    <TableRow>
      <TextTableCell content={data.code} />
      <TextTableCell content={data.name} />
      <DateTableCell value={data.fromDate} />
      <DateTableCell value={data.toDate} />
      <CheckboxTableCell checked={data.isActive} />
      <TextTableCell content={data.stateName} />
      <ActionsTableCell>
        <IconButton type={canWrite ? 'edit' : 'view'} onClick={handleEdit} disabled={pending} />
        <ListActionsMenu actions={actions} disabled={pending} />
      </ActionsTableCell>
    </TableRow>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object
};

export default ItemComponent;
